import { GetServerSideProps, GetStaticProps } from 'next'
import { useRouter } from 'next/router'

import stripUndefined from '../../utils/stripUndefined'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import { SiteLocale } from '../../graphql/datoSchema.generated'
import Layout from '../../components/Layout'
import BlockZone from '../../components/BlockZone'
import blocks from '../../blocks'
import { getPageTitle } from '../[[...slugs]]'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getPage, Page } from '../../graphql/page'
import { getSiteId } from '../../utils/env'
import { BaseExpertFragment, getExpertList } from '../../graphql/expertList'
import { expertsPerPage } from '../../sections/ExpertListingSection/ExpertListingSection.EMN'
import stringifyQueryString from '../../utils/stringifyQueryString'
import absolutify from '../../utils/absolutify'
import PageSeo from '../../components/PageSeo'
import { getAllDisciplines } from '../../graphql/disciplineList'
import { getAllSpecialisms } from '../../graphql/specialismList'
import { getAllNations } from '../../graphql/nationsList'
import { getAllExpertises } from '../../graphql/expertiseList'
import notEmpty from '../../utils/notEmpty'
import parseNextQuery from '../../utils/parseNextQuery'

export interface StaticProps {
  siteConfig: SiteConfig
  layout: LayoutType
  page: Page
  slug: string
}
interface PrivateStaticProps {
  disciplines: Awaited<ReturnType<typeof getAllDisciplines>>
  specialisms: Awaited<ReturnType<typeof getAllSpecialisms>>
  nations: Awaited<ReturnType<typeof getAllNations>>
  expertises: Awaited<ReturnType<typeof getAllExpertises>>
}

// next.js does not allow us to use both getServerSideProps and getStaticProps
// currently. This is coming, so I'll keep the structure in place.
// See https://github.com/vercel/next.js/discussions/11424#discussioncomment-2548409
const getStaticPropsFake: GetStaticProps<
  StaticProps & PrivateStaticProps,
  Record<string, never>
> = async ({ locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const siteId = getSiteId()

  const [siteConfig, layout, disciplines, specialisms, nations, expertises] =
    await Promise.all([
      getSiteConfig(siteId, locale as SiteLocale),
      getLayout(siteId, locale as SiteLocale),
      getAllDisciplines(locale as SiteLocale),
      getAllSpecialisms(locale as SiteLocale),
      getAllNations(locale as SiteLocale),
      getAllExpertises(locale as SiteLocale),
    ])

  // When unset, this site doesn't include an experts listing
  if (!siteConfig.expertsIndex?.slug) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const page = await getPage(
    siteId,
    locale as SiteLocale,
    siteConfig.expertsIndex.slug,
  )

  if (!page) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      layout,
      page,
      slug: siteConfig.expertsIndex.slug,
      disciplines,
      specialisms,
      nations,
      expertises,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

interface ServerProps {
  experts: BaseExpertFragment[]
  numTotalExperts: number
  currentPage: number
  disciplines: string[]
  specialisms: string[]
  nations: string[]
  expertises: string[]
}

export const getServerSideProps: GetServerSideProps<
  ServerProps,
  Record<string, never>
> = async (context) => {
  const staticProps = await getStaticPropsFake(context)
  if ('redirect' in staticProps) {
    return staticProps
  }
  if ('notFound' in staticProps) {
    // We are not allowed to return `revalidate` in this function
    return {
      notFound: true,
    }
  }

  const { res, query, locale } = context
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const siteId = getSiteId()

  const { disciplines, specialisms, nations, expertises } = staticProps.props
  const disciplineIds = parseNextQuery(query.disciplines)
    .map(
      (discipline) => disciplines.find((item) => item.name === discipline)?.id,
    )
    .filter(notEmpty)
  const specialismIds = parseNextQuery(query.specialisms)
    .map(
      (specialism) => specialisms.find((item) => item.name === specialism)?.id,
    )
    .filter(notEmpty)
  // The Model is nations but in the URL we want to show countries
  const nationIds = parseNextQuery(query.countries)
    .map((nation) => nations.find((item) => item.name === nation)?.id)
    .filter(notEmpty)
  const expertiseIds = parseNextQuery(query.expertises)
    .map((expertise) => expertises.find((item) => item.name === expertise)?.id)
    .filter(notEmpty)

  const { experts, count } = await getExpertList(
    locale as SiteLocale,
    disciplineIds,
    specialismIds,
    nationIds,
    expertiseIds,
    expertsPerPage,
    typeof query.page === 'string' ? Number(query.page) : undefined,
    siteId,
  )

  res.setHeader(
    'Cache-Control',
    'public, s-maxage=60, stale-while-revalidate=600',
  )

  return {
    props: stripUndefined({
      ...staticProps.props,
      disciplines: disciplines.map((item) => item.name).filter(notEmpty),
      specialisms: specialisms.map((item) => item.name).filter(notEmpty),
      nations: nations.map((item) => item.name).filter(notEmpty),
      expertises: expertises.map((item) => item.name).filter(notEmpty),
      experts,
      numTotalExperts: count,
      currentPage: Number(query.page) || 1,
    }),
  }
}

const useCanonical = (slug: string, page: number) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }

  return absolutify(
    page > 1
      ? `/${slug}?${stringifyQueryString(
          stripUndefined({
            page: page > 1 ? page : undefined,
            // TODO: Disciplines & specialisms
          }),
        )}`
      : `/${slug}`,
    locale as SiteLocale,
  )
}

const ExpertsIndex = ({
  siteConfig,
  layout,
  page,
  slug,
  experts,
  numTotalExperts,
  currentPage,
  disciplines,
  specialisms,
  nations,
  expertises,
}: StaticProps & ServerProps) => {
  const title = getPageTitle(page)
  const canonical = useCanonical(slug, currentPage)

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'experts',
          href: `/${slug}`,
          title: title || '?',
        },
      ]}
    >
      <PageSeo page={page} canonical={canonical} />

      <BlockZone
        components={blocks}
        content={page.content}
        data={{
          ExpertListingSection: {
            experts,
            numTotalExperts,
            currentPage,
            disciplines,
            specialisms,
            nations,
            expertises,
          },
        }}
      />
    </Layout>
  )
}

export default ExpertsIndex
